import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { X, ShieldOff } from "lucide-react";
import { manageApprovalsApi } from "../../../lib/manageApprovalsApi";
import type { ApprovalControlRow } from "../../../lib/manageApprovalsApi";
import type { ApprovalDenies } from "../../../types/index";
import {
  APPROVAL_SCOPES,
  APPROVAL_SCOPE_LABELS,
  APPROVAL_ACTION_LABELS,
} from "../../../types/pages";
import type { ApprovalScope, ApprovalAction } from "../../../types/pages";

interface Props {
  businessId: string;
  row: ApprovalControlRow;
  onClose: () => void;
  onSaved: () => void;
  onError: () => void;
}

export function ApprovalControlModal({ businessId, row, onClose, onSaved, onError }: Props) {
  const queryClient = useQueryClient();
  const [denies, setDenies] = useState<ApprovalDenies>(() =>
    // Clone so edits don't mutate the query cache.
    Object.fromEntries(
      Object.entries(row.approvalDenies).map(([scope, actions]) => [scope, [...(actions ?? [])]]),
    ) as ApprovalDenies,
  );

  const isDenied = (scope: ApprovalScope, action: ApprovalAction): boolean =>
    denies[scope]?.includes(action) ?? false;

  const toggle = (scope: ApprovalScope, action: ApprovalAction) => {
    setDenies((prev) => {
      const current = prev[scope] ?? [];
      const next = current.includes(action)
        ? current.filter((a) => a !== action)
        : [...current, action];
      const updated = { ...prev };
      if (next.length) updated[scope] = next;
      else delete updated[scope];
      return updated;
    });
  };

  const mutation = useMutation({
    mutationFn: () => manageApprovalsApi.set(businessId, row.userId, denies),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["approval-controls", businessId] });
      onSaved();
    },
    onError,
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-lg rounded-xl bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-start justify-between border-b border-[#f1f5f9] px-6 py-4">
          <div>
            <h2 className="text-sm font-semibold text-[#0f172a]">{row.name}</h2>
            <p className="mt-0.5 text-xs text-[#64748b]">
              {row.roleName ? `${row.roleName} · ` : ""}Tick an action to remove it. Unticked = allowed.
            </p>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-md text-[#94a3b8] hover:bg-[#f1f5f9] hover:text-[#475569]"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Scopes */}
        <div className="max-h-[60vh] overflow-y-auto px-6 py-4 space-y-4">
          {row.scopes.map((scope) => (
            <div key={scope} className="rounded-lg border border-[#e2e8f0] p-3">
              <p className="text-sm font-medium text-[#0f172a] mb-2">{APPROVAL_SCOPE_LABELS[scope]}</p>
              <div className="flex flex-wrap gap-x-5 gap-y-2">
                {APPROVAL_SCOPES[scope].actions.map((action) => {
                  const denied = isDenied(scope, action);
                  return (
                    <label
                      key={action}
                      className="flex cursor-pointer items-center gap-2 text-sm text-[#475569]"
                    >
                      <input
                        type="checkbox"
                        checked={denied}
                        onChange={() => toggle(scope, action)}
                        className="h-4 w-4 rounded border-[#cbd5e1] text-[#dc2626] focus:ring-[#dc2626]"
                      />
                      <span className={denied ? "text-[#dc2626] line-through" : ""}>
                        {APPROVAL_ACTION_LABELS[action]}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-[#f1f5f9] px-6 py-4">
          <div className="flex items-center gap-1.5 text-xs text-[#94a3b8]">
            <ShieldOff className="h-3.5 w-3.5" />
            Ticked actions are blocked for this member.
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              disabled={mutation.isPending}
              className="rounded-lg border border-[#e2e8f0] px-4 py-2 text-sm font-medium text-[#475569] hover:bg-[#f8fafc] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
              className="rounded-lg bg-[#2447d9] px-4 py-2 text-sm font-medium text-white hover:bg-[#1e3bb8] disabled:opacity-50"
            >
              {mutation.isPending ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
